import {
  Component,
  ViewChild,
  AfterViewInit,
  effect,
  Injector,
  signal,
  computed,
  WritableSignal,
  Signal,
  inject,
} from '@angular/core';
import { NavService } from '@app/core/services/nav.service';
import { LayoutService } from '@app/core/services/layout.service';
import { Popover } from 'primeng/popover';
import { Button } from 'primeng/button';
import { UserService } from '@app/core/services/user.service';
import { Router } from '@angular/router';
import { PermissionService } from '@app/core/services/permission.service';
import { NotificationsDataService } from '@app/account/notifications/services/notifications-data.service';

import { PermissionEnum } from '@app/generated/graphql';
import { IAlertTestItem } from '@app/account/notifications/interfaces/notifications-data.interfaces';

@Component({
  selector: 'app-global-layout',
  templateUrl: './global-layout.component.html',
  standalone: false,
})
export class GlobalLayoutComponent implements AfterViewInit {
  static readonly FIRST_TIME_POPOVER_DISMISSED_CACHE_NAME =
    'navigation-first-time-popover-dismissed';

  PermissionEnum = PermissionEnum;

  private _notificationsDataService = inject(NotificationsDataService);

  /** Alerts from notifications service – single source of truth for desktop and mobile. */
  readonly alerts = this._notificationsDataService.alerts;
  readonly notifications = this._notificationsDataService.notifications;

  @ViewChild('firstTimeTarget')
  firstTimeTarget: Button;

  @ViewChild('firstTimePopover')
  firstTimePopover: Popover;

  firstTimePopoverDismissed: WritableSignal<boolean> = signal(false);
  showFirstTimePopover: Signal<boolean> = computed(
    () =>
      !this.firstTimePopoverDismissed() &&
      !this.navService.isSidebarOpen() &&
      this.navService.isNavigationEnabled(),
  );

  constructor(
    public navService: NavService,
    public layoutService: LayoutService,
    private injector: Injector,
    public userService: UserService,
    private router: Router,
    public permissionService: PermissionService,
  ) {
    this.initFirstTimePopover();
  }

  ngAfterViewInit(): void {
    // watch for sidebar state changes which are linked to the view being rendered
    this.onSidebarStateChange();
  }

  /**
   * Effect that watches the sidebar state and opens/closes the popover accordingly.
   */
  onSidebarStateChange(): void {
    // This effect triggers as a biproduct of the sidebar state change
    effect(
      () => {
        // if the popover is not initialized, exit the effect
        if (!this.firstTimePopover) {
          return;
        }

        if (this.showFirstTimePopover()) {
          this.openFirstTimePopover();
        } else {
          this.firstTimePopover.hide();
        }
      },
      // pass lifecycle injector to the effect for easy cleanup
      { injector: this.injector },
    );
  }

  initFirstTimePopover(): void {
    const firstTimePopoverDismissed = this.getLocalStorage(
      GlobalLayoutComponent.FIRST_TIME_POPOVER_DISMISSED_CACHE_NAME,
    );
    this.firstTimePopoverDismissed.set(!!firstTimePopoverDismissed);
  }

  openFirstTimePopover(): void {
    if (
      this.firstTimePopover &&
      this.firstTimeTarget &&
      !this.firstTimePopover.overlayVisible
    ) {
      this.firstTimePopover.show(null, this.firstTimeTarget.el.nativeElement);
    }
  }

  dismissFirstTimePopover(): void {
    this.firstTimePopoverDismissed.set(true);
    this.firstTimePopover.hide();

    this.setLocalStorage(
      true,
      GlobalLayoutComponent.FIRST_TIME_POPOVER_DISMISSED_CACHE_NAME,
    );
  }

  setLocalStorage(value: any, name: string): void {
    localStorage.setItem(name, JSON.stringify(value));
  }

  getLocalStorage(name: string): any | null {
    const value = localStorage.getItem(name);
    return value ? JSON.parse(value) : null;
  }

  onSettings(): void {
    this.navService.openSidebar();
    this.navService.openSettings();
    this.router.navigate(['/account/settings']);
  }

  // Alert-specific handlers
  onMarkAllAlertsAsRead(): void {
    this._notificationsDataService.markAllAlertsAsRead();
  }

  onArchiveAllReadAlerts(): void {
    this._notificationsDataService.archiveAllReadAlerts();
  }

  onArchiveAllAlerts(): void {
    this._notificationsDataService.archiveAllAlerts();
  }

  onToggleAlertMarkAsRead(alert: IAlertTestItem): void {
    this._notificationsDataService.toggleAlertMarkAsRead(alert);
  }

  onArchiveAlert(alert: IAlertTestItem): void {
    this._notificationsDataService.archiveAlert(alert);
  }

  onAlertClick(alert: IAlertTestItem): void {
    if (!alert) {
      return;
    }

    this._notificationsDataService.markAlertAsRead(alert);

    if (alert?.targetUrl) {
      this.router.navigate([alert.targetUrl]);
    }
  }

  // Notification-specific handlers
  onMarkAllNotificationsAsRead(): void {
    this._notificationsDataService.markAllNotificationsAsRead();
  }

  onArchiveAllReadNotifications(): void {
    this._notificationsDataService.archiveAllReadNotifications();
  }

  onArchiveAllNotifications(): void {
    this._notificationsDataService.archiveAllNotifications();
  }

  onToggleNotificationMarkAsRead(notification: IAlertTestItem): void {
    this._notificationsDataService.toggleNotificationMarkAsRead(notification);
  }

  onArchiveNotification(notification: IAlertTestItem): void {
    this._notificationsDataService.archiveNotification(notification);
  }

  onNotificationClick(notification: IAlertTestItem): void {
    if (!notification) {
      return;
    }

    this._notificationsDataService.markNotificationAsRead(notification);

    if (notification?.targetUrl) {
      this.router.navigate([notification.targetUrl]);
    }
  }
}
